import {media, Spacer, spacing, translucify} from '@nfq/react-grid';
import Image from 'next/image';
import {useTranslation} from 'next-i18next';
import styled from 'styled-components';

import {Text} from 'UI/components/layout/ultra/Text';

import {UltraBibtights, UltraCap, UltraGloves, UltraHelmet, UltraJersey, UltraSocks} from 'Images/ultra';

/**
 * Renders an outfit grid displaying various outfit components such as jersey, bibtights, gloves, cap, helmet, and socks.
 * Each outfit component is accompanied by its respective headline and description points retrieved from translations.
 *
 * @returns The JSX element representing the outfit grid.
 *
 * @example
 * ```tsx
 * <OutfitGrid />
 * ```
 */
export const OutfitGrid = () => {
    const {t} = useTranslation('ultra', {keyPrefix: 'details.equipment'});

    return (
        <Grid>
            <GridCell $orientation="col">
                <GridCellImage alt="" src={UltraJersey} unoptimized />
                <div>
                    <GridCellHeadline>{t('outfit.jersey.headline')}</GridCellHeadline>
                    <Spacer y={{lg: 8, xs: 4}} />
                    <ValueWrapper>
                        <TextLi>{t('outfit.jersey.point1')}</TextLi>
                        <TextLi>{t('outfit.jersey.point2')}</TextLi>
                        <TextLi>{t('outfit.jersey.point3')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridCell>
            <GridCell $orientation="row">
                <GridCellImage alt="" src={UltraBibtights} unoptimized />
                <div>
                    <GridCellHeadline>{t('outfit.bibtights.headline')}</GridCellHeadline>
                    <Spacer y={{lg: 8, xs: 4}} />
                    <ValueWrapper>
                        <TextLi>{t('outfit.bibtights.point1')}</TextLi>
                        <TextLi>{t('outfit.bibtights.point2')}</TextLi>
                        <TextLi>{t('outfit.bibtights.point3')}</TextLi>
                        <TextLi>{t('outfit.bibtights.point4')}</TextLi>
                        <TextLi>{t('outfit.bibtights.point5')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridCell>
            <GridCell $orientation="row">
                <GridCellImage alt="" src={UltraGloves} unoptimized />
                <div>
                    <GridCellHeadline>{t('outfit.gloves.headline')}</GridCellHeadline>
                    <Spacer y={{lg: 8, xs: 4}} />
                    <ValueWrapper>
                        <TextLi>{t('outfit.gloves.point1')}</TextLi>
                        <TextLi>{t('outfit.gloves.point2')}</TextLi>
                        <TextLi>{t('outfit.gloves.point3')}</TextLi>
                        <TextLi>{t('outfit.gloves.point4')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridCell>
            <GridCell $orientation="row">
                <GridCellImage alt="" src={UltraCap} unoptimized />
                <div>
                    <GridCellHeadline>{t('outfit.cap.headline')}</GridCellHeadline>
                    <Spacer y={{lg: 8, xs: 4}} />
                    <ValueWrapper>
                        <TextLi>{t('outfit.cap.point1')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridCell>
            <GridCell $orientation="col">
                <GridCellImage alt="" src={UltraHelmet} unoptimized />
                <div>
                    <GridCellHeadline>{t('outfit.helmet.headline')}</GridCellHeadline>
                    <Spacer y={{lg: 8, xs: 4}} />
                    <ValueWrapper>
                        <TextLi>{t('outfit.helmet.point1')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridCell>
            <GridCell $orientation="row">
                <GridCellImage alt="" src={UltraSocks} unoptimized />
                <div>
                    <GridCellHeadline>{t('outfit.socks.headline')}</GridCellHeadline>
                    <Spacer y={{lg: 8, xs: 4}} />
                    <ValueWrapper>
                        <TextLi>{t('outfit.socks.point1')}</TextLi>
                    </ValueWrapper>
                </div>
            </GridCell>
        </Grid>
    );
};

const Grid = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};

    ${media('xl')} {
        display: grid;
        gap: ${spacing(4)};
        grid-template-columns: repeat(3, 1fr);
    }
`;

interface GridCellProps {
    $orientation: 'col' | 'row';
}

const GridCell = styled.div<GridCellProps>`
    align-items: center;
    background: #1C1C1C;
    ${({$orientation}) => ($orientation === 'col' ? 'grid-row: span 2' : 'grid-column: span 2')};
    border: 1px solid ${({theme}) => translucify(theme.colors.ultraPageForeground, 30)};
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    font-size: 90px;
    gap: ${spacing(6)};
    justify-content: center;
    padding: ${spacing(10)} ${spacing(6)};

    ${media('md')} {
        border-radius: 32px;
    }

    ${media('xl')} {
        flex-direction: ${({$orientation}) => ($orientation === 'col' ? 'column' : 'row')};
        gap: ${spacing(10)};
        padding: ${spacing(10)};
    }
`;

const GridCellHeadline = styled(Text)`
    color: ${({theme}) => theme.colors.ultraPageForeground};
    font-weight: 700;
    text-transform: uppercase;
`;

const GridCellImage = styled(Image)`
    display: block;
    height: 185px;
    width: auto;

    ${media('xxl')} {
        height: 278px;
    }
`;

const ValueWrapper = styled.ul`
    margin: 0;
    padding-inline-start: 3rem;
`;

const TextLi = styled(Text).attrs({as: 'li'})`
    font-size: 1.4rem;
    line-height: 1.8rem;

    ${media('lg')} {
        font-size: 2rem;
        line-height: 2.6rem;
    }
`;